// The cut face of the flesh as real geometry: a strip stood on each edge of the carving, from the skin in through the
// wall's thickness to the inner wall. The skin and the inner wall are both opened where the carving says (looked up
// by the skin's sheet point), so the strip runs from one's hole edge to the other's and closes the wall.

import { STRIDE, type PumpkinGeometry } from './shape';

const TAU = Math.PI * 2;

/** the skin by direction: for each cell of an equirectangular grid of the sheet, the point and normal there */
export interface SheetMap { w: number; h: number; data: Float32Array }

/**
 * Each triangle of the skin drawn into the sheet's grid by its sheet points (the neck unbent), carrying the real
 * position and the normal, interpolated as the GPU does across that triangle. Cells no triangle's centre test caught
 * (round the poles, where the rows fan) take the nearest above or below
 */
export function sheetMap(g: PumpkinGeometry, squash: number, w = 512): SheetMap {
  const h = w / 2, data = new Float32Array(w * h * 6), got = new Uint8Array(w * h), V = g.verts, I = g.index;
  const n = V.length / STRIDE, xs = new Float64Array(n), ys = new Float64Array(n), pole = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * STRIDE, qx = V[o + 10], qy = V[o + 11], qz = V[o + 12], r = Math.hypot(qx, qz);
    xs[i] = (Math.atan2(qx, qz) / TAU + 0.5) * w;
    ys[i] = (0.5 - Math.atan2(qy / squash, r) / Math.PI) * h;
    pole[i] = r < 1e-6 ? 1 : 0;
  }
  const X = [0, 0, 0], Y = [0, 0, 0], ix = [0, 0, 0];
  for (let t = 0; t < I.length; t += 3) {
    for (let k = 0; k < 3; k++) { ix[k] = I[t + k]; X[k] = xs[ix[k]]; Y[k] = ys[ix[k]]; }
    // a pole point has no direction round: it takes the others' (unwrapped against each other first)
    const real = [0, 1, 2].filter(k => !pole[ix[k]]);
    if (!real.length) continue;
    for (const k of real) if (Math.abs(X[k] - X[real[0]]) > w / 2) X[k] += X[k] < X[real[0]] ? w : -w;
    const mx = real.reduce((a, k) => a + X[k], 0) / real.length;
    for (let k = 0; k < 3; k++) if (pole[ix[k]]) X[k] = mx;
    const den = (Y[1] - Y[2]) * (X[0] - X[2]) + (X[2] - X[1]) * (Y[0] - Y[2]);
    if (Math.abs(den) < 1e-12) continue;
    const x0 = Math.floor(Math.min(X[0], X[1], X[2]) - 0.5), x1 = Math.ceil(Math.max(X[0], X[1], X[2]) - 0.5);
    const y0 = Math.max(0, Math.floor(Math.min(Y[0], Y[1], Y[2]) - 0.5)), y1 = Math.min(h - 1, Math.ceil(Math.max(Y[0], Y[1], Y[2]) - 0.5));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      const px = x + 0.5, py = y + 0.5;
      const a = ((Y[1] - Y[2]) * (px - X[2]) + (X[2] - X[1]) * (py - Y[2])) / den;
      const b = ((Y[2] - Y[0]) * (px - X[2]) + (X[0] - X[2]) * (py - Y[2])) / den;
      const c = 1 - a - b;
      if (a < -1e-4 || b < -1e-4 || c < -1e-4) continue;
      const cell = y * w + (((x % w) + w) % w), o = cell * 6;
      for (let k = 0; k < 6; k++) data[o + k] = a * V[ix[0] * STRIDE + k] + b * V[ix[1] * STRIDE + k] + c * V[ix[2] * STRIDE + k];
      got[cell] = 1;
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      if (got[y * w + x]) continue;
      let k = 1;
      for (; k < h; k++) {
        const up = y - k, dn = y + k;
        const from = up >= 0 && got[up * w + x] ? up : dn < h && got[dn * w + x] ? dn : -1;
        if (from >= 0) { data.copyWithin((y * w + x) * 6, (from * w + x) * 6, (from * w + x) * 6 + 6); break; }
      }
    }
  }
  return { w, h, data };
}

/** the point and normal on the skin at (x, y) in a picture of W by H pixels, bilinear between cells; into out */
function at(m: SheetMap, W: number, H: number, x: number, y: number, out: Float64Array) {
  const fx = (x / W) * m.w - 0.5, fy = Math.min(m.h - 1, Math.max(0, (y / H) * m.h - 0.5));
  const i0 = Math.floor(fx), j0 = Math.min(m.h - 2, Math.floor(fy)), tx = fx - i0, ty = fy - j0;
  const a = ((i0 % m.w) + m.w) % m.w, b = (a + 1) % m.w, D = m.data;
  const c00 = (j0 * m.w + a) * 6, c10 = (j0 * m.w + b) * 6, c01 = ((j0 + 1) * m.w + a) * 6, c11 = ((j0 + 1) * m.w + b) * 6;
  for (let k = 0; k < 6; k++) {
    out[k] = (D[c00 + k] * (1 - tx) + D[c10 + k] * tx) * (1 - ty) + (D[c01 + k] * (1 - tx) + D[c11 + k] * tx) * ty;
  }
}

/** floats per vertex of the strip: position, normal, how far in through the wall (0 at the skin, 1 at the inner wall) */
export const WALL_STRIDE = 7;

/**
 * The strip over every segment of the carving's edges (carve.ts `traceEdges`: x, y, gx, gy at each end, in the
 * picture's pixels). Each end is set a third of a pixel back from the edge into the flesh, so the strip starts under
 * the skin's own edge and no crack of light opens between them. Its normal faces into the hole
 */
export function wallMesh(m: SheetMap, W: number, H: number, tiles: (Float32Array | null)[], wall: number): { v: Float32Array; i: Uint32Array } {
  let segs = 0;
  for (const t of tiles) if (t) segs += t.length / 8;
  const v = new Float32Array(segs * 4 * WALL_STRIDE), idx = new Uint32Array(segs * 6);
  const P = new Float64Array(6), A = new Float64Array(6), B = new Float64Array(6), C = new Float64Array(6);
  let nv = 0, ni = 0;
  const end = (x: number, y: number, gx: number, gy: number) => {
    const gl = Math.hypot(gx, gy) || 1, ux = gx / gl, uy = gy / gl;
    const sx = x - ux * 0.35, sy = y - uy * 0.35;
    at(m, W, H, sx, sy, P);
    // along the edge, and toward the hole, on the skin
    at(m, W, H, sx - uy * 0.5, sy + ux * 0.5, A);
    at(m, W, H, sx + uy * 0.5, sy - ux * 0.5, B);
    at(m, W, H, sx + ux * 0.5, sy + uy * 0.5, C);
    const tx = A[0] - B[0], ty = A[1] - B[1], tz = A[2] - B[2];
    const dx = -P[3] * wall, dy = -P[4] * wall, dz = -P[5] * wall;
    let nx = ty * dz - tz * dy, ny = tz * dx - tx * dz, nz = tx * dy - ty * dx;
    const nl = Math.hypot(nx, ny, nz) || 1;
    if (nx * (C[0] - P[0]) + ny * (C[1] - P[1]) + nz * (C[2] - P[2]) < 0) { nx = -nx; ny = -ny; nz = -nz; }
    nx /= nl; ny /= nl; nz /= nl;
    for (const k of [0, 1]) {
      const o = nv * WALL_STRIDE;
      v[o] = P[0] + dx * k; v[o + 1] = P[1] + dy * k; v[o + 2] = P[2] + dz * k;
      v[o + 3] = nx; v[o + 4] = ny; v[o + 5] = nz; v[o + 6] = k;
      nv++;
    }
  };
  for (const t of tiles) {
    if (!t) continue;
    for (let s = 0; s < t.length; s += 8) {
      const base = nv;
      end(t[s], t[s + 1], t[s + 2], t[s + 3]);
      end(t[s + 4], t[s + 5], t[s + 6], t[s + 7]);
      idx.set([base, base + 1, base + 2, base + 1, base + 3, base + 2], ni); ni += 6;
    }
  }
  return { v, i: idx };
}
